import React from "react";

import downArrow from "../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../assets/images/svg/mab-ellipse.svg";
import audioFrame from "../assets/images/svg/audio-frame.svg";
import audioMobileFrame from "../assets/images/svg/audio-mobile-frame.svg";
import warningIcon from "../assets/images/svg/warning.svg";
import NextButton from "./common/next-button";
import StepNumberBadge from "./common/StepNumberBadge";

type AudioDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  audioFileName: string;
  onAudioUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAudioUploading?: boolean;
  audioUploadProgress?: number;
  onAudioRemove?: () => void;
  isEditMode?: boolean;
  showValidationMessage?: boolean;
  validationMessage?: string;
};

const AudioDropdown: React.FC<AudioDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  audioFileName,
  onAudioUploadChange,
  isAudioUploading,
  audioUploadProgress,
  onAudioRemove,
  isEditMode = false,
  showValidationMessage = false,
  validationMessage,
}) => {
  const isOpen = expandedId === id;
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditMode) return;
    if (e.target.files && e.target.files.length > 0) {
      onAudioUploadChange(e);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* HEADER */}
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <StepNumberBadge stepNumber={2} isOpen={isOpen} />
            <span
              className="text-[16px] lg:text-[20px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Audio
            </span>
          </div>

          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {/* BODY */}
        {isOpen && (
          <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6 md:ml-[41px]">
            {/* Title & subtitle */}
            <div className="flex flex-col gap-1">
              <span className="text-white text-[14px] lg:text-[16px] font-normal font-[Poppins] leading-[130%]">
                Upload audio
              </span>
              <span className="text-[#8FA5BF] text-[14px] lg:text-[16px] font-[Poppins] leading-[150%]">
                Upload your song for ad setup
              </span>
            </div>

            {/* Drop zone */}
            {!audioFileName && !isEditMode && (
              <>
                {/* ── MOBILE drop zone (audioMobileFrame SVG as border) ── */}
                <label
                  htmlFor="audio-upload"
                  className="relative w-full flex flex-col items-center justify-center cursor-pointer rounded-[14px] md:hidden mb-3"
                  style={{
                    minHeight: "200px",
                    backgroundImage: `url(${audioMobileFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                ></label>

                {/* ── DESKTOP drop zone (image frame) ── */}
                <label
                  htmlFor="audio-upload"
                  className="relative w-full min-h-[200px] lg:min-h-[260px] hidden md:flex flex-col items-center justify-center cursor-pointer rounded-[14px] overflow-hidden"
                  style={{
                    backgroundImage: `url(${audioFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                />
              </>
            )}

            <input
              ref={fileInputRef}
              id="audio-upload"
              type="file"
              accept=".mp3,.wav"
              className="hidden"
              disabled={isEditMode}
              onChange={handleFileChange}
            />

            {/* UPLOAD STATUS */}
            {(isAudioUploading || audioFileName) && (
              <div className="w-full rounded-[14px] bg-[#121420] border border-[#2A2F3D] p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium font-[Poppins]">
                    {isAudioUploading ? "Uploading..." : "Uploaded audio"}
                  </span>
                  {onAudioRemove && !isEditMode && (
                    <button
                      onClick={onAudioRemove}
                      className="text-xs text-[#EE6161] font-medium hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {isAudioUploading && (
                  <div className="w-full h-2 rounded-full bg-[#1E2433] overflow-hidden">
                    <div
                      className="h-full bg-[#58F2A9]"
                      style={{ width: `${audioUploadProgress ?? 0}%` }}
                    />
                  </div>
                )}

                {!isAudioUploading && audioFileName && (
                  <span className="text-[#58F2A9] text-xs font-[Poppins]">
                    ✓ Audio uploaded successfully!
                  </span>
                )}

                {audioFileName && (
                  <span className="text-[#878DA1] text-xs font-[Poppins] break-all">
                    {audioFileName}
                  </span>
                )}
              </div>
            )}
  {showValidationMessage && validationMessage && (
              <div className="flex items-center gap-2 text-[#EE404C] text-[13px] font-normal">
                <img src={warningIcon} alt="" className="w-[16px] h-[16px]" />
                <span>{validationMessage}</span>
              </div>
            )}
            {/* NEXT BUTTON */}
            <div className="flex justify-end w-full mt-1">
              <NextButton
                size="sm"
                className="w-full md:w-auto"
                onClick={() => onNext?.(id)}
              />
            </div>
          
            {isEditMode && (
              <span className="text-[#8FA5BF] text-xs font-[Poppins]">
                Audio is locked in edit mode.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioDropdown;
